import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { RefreshCwIcon } from 'lucide-react';
import type { TestMcpConnectionResponseDto } from '@inference.engine/api-client/web';

import { useTestMcpConnection } from '../../model';
import { useTranslation } from 'react-i18next';

type ErrorType = NonNullable<TestMcpConnectionResponseDto['errorType']>;

const ERROR_LABEL_KEYS = {
  unreachable: 'userMcpServers.mcpHealthBadge.unreachable',
  unauthorized: 'userMcpServers.mcpHealthBadge.authFailed',
  // Distinct from authFailed on purpose: the server took the credentials and refused anyway, so
  // reconnecting achieves nothing. The detail on hover carries whatever the server said.
  forbidden: 'userMcpServers.mcpHealthBadge.accessDenied',
  not_an_mcp: 'userMcpServers.mcpHealthBadge.notAnMcpServer',
  timeout: 'userMcpServers.mcpHealthBadge.timedOut',
} as const satisfies Record<ErrorType, string>;

/**
 * Live connection-health indicator for an installed MCP: a colored dot + label (Connected / a
 * categorized error with the detail on hover) and a manual refresh button. Reads the cached
 * `/test` result; refresh re-checks.
 */
export const McpHealthBadge = ({ mcpCatalogId }: { mcpCatalogId: string }) => {
  const { t } = useTranslation('admin');

  const { data, isFetching, refetch } = useTestMcpConnection(mcpCatalogId);

  const connected = data?.status === 'connected';
  const label = isFetching
    ? t('userMcpServers.chatMcpServers.checking')
    : connected
      ? t('userMcpServers.chatMcpServers.connected')
      : t(
          data?.errorType
            ? ERROR_LABEL_KEYS[data.errorType]
            : 'userMcpServers.mcpHealthBadge.error',
        );
  const toneClass = isFetching
    ? 'text-muted-foreground'
    : connected
      ? 'text-positive'
      : 'text-destructive';
  const dotClass = isFetching
    ? 'bg-muted-foreground'
    : connected
      ? 'bg-positive'
      : 'bg-destructive';

  // A refused request is the one state where the obvious next action is the wrong one, so it gets a
  // sentence of its own. Whatever the server said follows it, when it said anything worth reading.
  const detail = [
    data?.errorType === 'forbidden'
      ? t('userMcpServers.mcpHealthBadge.accessDeniedHint')
      : undefined,
    // Attributed and quoted rather than run together with ours: this text is written by the third
    // party we failed to reach, and must not read as advice from us.
    data?.errorMessage
      ? t('userMcpServers.mcpHealthBadge.serverSaid', { detail: data.errorMessage })
      : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  const labelEl = <span className={`text-body-sm whitespace-nowrap ${toneClass}`}>{label}</span>;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1.5">
        {isFetching ? (
          <Spinner size="sm" aria-hidden className="shrink-0" />
        ) : (
          <span className={`size-2 shrink-0 rounded-full ${dotClass}`} />
        )}
        {!isFetching && !connected && detail ? (
          <Tooltip>
            <TooltipTrigger asChild>{labelEl}</TooltipTrigger>
            <TooltipContent>{detail}</TooltipContent>
          </Tooltip>
        ) : (
          labelEl
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-mini"
              aria-label={t('userMcpServers.mcpHealthBadge.refreshConnectionStatus')}
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCwIcon className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('userMcpServers.mcpHealthBadge.reCheckConnection')}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};
